/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { Fragment, useEffect, useState } from 'react';
import {
  Input,
  message,
  Modal,
  Select,
  Form,
  InputNumber,
  Radio,
  Checkbox,
} from 'antd';
import { useIntl } from 'react-intl';
import { ModalProps } from 'antd/lib/modal';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { useStyles } from '~src/hooks/useStyles';
import { styleFn } from '~components/molecules/RightSideNav/Modals/ModalFormStyles.style';
import { Controller, useForm } from 'react-hook-form';
import { updateVoucher, updateVoucherGroup } from '~src/services/api/contracts';
import { queryCache, useMutation } from 'react-query';
import { appLog } from '~src/utils/log';
const { Option } = Select;

type DbVoucher = {
  id: number;
  name: string;
  description?: string;
  voucher_type: 1 | 2 | 3 | 4;
  unit_count?: number;
  unit_type?: number;
  is_voucher_group: boolean;
  voucher_group_option?: {
    voucher_group_contract_id?: number;
    sub_voucher_ids: number[];
  };
  attachments?: any[];
  sort?: number;
  tender_voucher_id?: number;
  state?: number;
  is_optional?: boolean;
};

type DbVoucherGroupOrCard = DbVoucher;

type EditModalProps = Pick<ModalProps, 'visible'> & {
  onCancel?: () => void;
  onOk?: () => void;
  projectId: number;
  tenderId: number;
  contractId?: any;
  voucherId: number;
  previousVoucherData: {
    data: DbVoucherGroupOrCard;
    type: 'group' | 'card';
  };
};

const unitTypes = [
  { value: 1, label: 'Centimeter' },
  { value: 2, label: 'Meter' },
  { value: 3, label: 'Square Meter' },
  { value: 4, label: 'Cubic Meter' },
  { value: 5, label: 'Piece' },
  { value: 6, label: 'Sum' },
  { value: 7, label: 'Kilogram' },
  { value: 8, label: 'Ton' },
  { value: 9, label: 'Timer' },
  { value: 10, label: 'Day' },
  { value: 11, label: 'Week' },
  { value: 12, label: 'Month' },
];

export const ContractBuilderEditModal: React.FC<EditModalProps> = ({
  onCancel,
  onOk,
  projectId,
  tenderId,
  contractId,
  voucherId,
  previousVoucherData,
  ...modalProps
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const [selectedType, setSelectedType] = useState<1 | 2 | 3 | 4>(
    previousVoucherData?.data?.voucher_type || 1
  );

  const { type: itemType, data: voucherData } = previousVoucherData || {};
  const isGroup = itemType === 'group';

  // Initialize form with default values
  const voucherForm = useForm({
    defaultValues: {
      name: voucherData?.name || '',
      description: voucherData?.description || '',
      voucher_type: voucherData?.voucher_type || 1,
      unit_count: voucherData?.unit_count || 0,
      unit_type: voucherData?.unit_type || 0,
      sort: voucherData?.sort,
      is_optional: voucherData?.is_optional ?? false,
    },
  });

  const [doUpdateVoucher, { isLoading: isUpdatingVoucher }] = useMutation(
    updateVoucher,
    {
      onSuccess: () => {
        queryCache.invalidateQueries([projectId, tenderId, contractId]);
        message.success(
          intl.formatMessage({ id: 'voucher-updated-successfully' })
        );
        onOk?.();
      },
      onError: (error, item) => {
        message.error(intl.formatMessage({ id: 'share-edit-failed' }));
        appLog('updateShareMutation -> item', item);
        appLog('updateShareMutation -> err', error);
      },
    }
  );

  const [
    doUpdateVoucherGroup,
    { isLoading: isUpdatingVoucherGroup },
  ] = useMutation(updateVoucherGroup, {
    onSuccess: () => {
      queryCache.invalidateQueries([projectId, tenderId, contractId]);
      message.success(
        intl.formatMessage({ id: 'voucher-updated-successfully' })
      );
      onOk?.();
    },
    onError: (error, item) => {
      message.error(intl.formatMessage({ id: 'share-edit-failed' }));
      appLog('updateShareMutation -> item', item);
      appLog('updateShareMutation -> err', error);
    },
  });

  useEffect(() => {
    if (voucherData) {
      voucherForm.reset({
        name: voucherData.name || '',
        description: voucherData.description || '',
        voucher_type: voucherData.voucher_type || 1,
        unit_count: voucherData.unit_count || 0,
        unit_type: voucherData.unit_type || 0,
        sort: voucherData.sort,
        is_optional: voucherData.is_optional ?? false,
      });
      setSelectedType(voucherData.voucher_type);
    }
  }, [voucherData]);

  const isValid = () => {
    const values = voucherForm.getValues();

    if (!values.name) {
      return false;
    }

    if (!isGroup) {
      if (
        values.voucher_type === 1 &&
        (values.unit_count === undefined || values.unit_count < 0)
      ) {
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    try {
      if (!isValid()) {
        message.error(intl.formatMessage({ id: 'fill-required-fields' }));
        return;
      }

      const formData = voucherForm.getValues();
      appLog('Submitting form data:', formData);

      if (isGroup) {
        await doUpdateVoucherGroup({
          projectId,
          tenderId,
          contractId,
          voucherId,
          data: {
            name: formData.name,
            description: formData.description,
            sort: formData.sort,
          },
        });
      } else {
        await doUpdateVoucher({
          projectId,
          tenderId,
          contractId,
          voucherId,
          data: {
            name: formData.name,
            description: formData.description,
            unit_count: formData.unit_count,
            unit_type: formData.unit_type,
            sort: formData.sort,
            is_optional: formData.is_optional,
          },
        });
      }
    } catch (err) {
      console.error('Error updating voucher:', err);
      message.error(intl.formatMessage({ id: 'failed-update-voucher' }));
    }
  };

  const handleCancel = () => {
    voucherForm.reset();
    onCancel?.();
  };

  return (
    <Modal
      title={
        <ModalTitle
          text={
            isGroup
              ? intl.formatMessage({ id: 'edit-group' })
              : intl.formatMessage({ id: 'edit-card' })
          }
          icon={['far', 'pen']}
        />
      }
      okText={intl.formatMessage({ id: 'save' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      centered
      okButtonProps={{
        disabled: !isValid(),
        loading: isGroup ? isUpdatingVoucherGroup : isUpdatingVoucher,
      }}
      onOk={handleSubmit}
      onCancel={handleCancel}
      destroyOnClose
      zIndex={2000}
      width={500}
      {...modalProps}>
      <div css={styles.formContainer}>
        {/* Name field - for both Group and Card */}
        <div css={styles.formItem}>
          <p css={styles.formLabel}>{intl.formatMessage({ id: 'name' })}</p>
          {/* @ts-ignore */}
          <Controller
            name="name"
            control={voucherForm.control}
            render={({ field }) => <Input {...field} />}
          />
        </div>

        {/* Fields only for Cards */}
        {!isGroup && (
          <Fragment>
            {/* Description field */}
            <div css={styles.formItem}>
              <p css={styles.formLabel}>
                {intl.formatMessage({ id: 'description' })}
              </p>
              <Controller
                name="description"
                control={voucherForm.control}
                render={({ field }) => (
                  <Input.TextArea
                    {...field}
                    autoSize={{ minRows: 3, maxRows: 5 }}
                  />
                )}
              />
            </div>

            {/* Optional voucher field */}
            <div css={styles.formItem}>
              <Controller
                name="is_optional"
                control={voucherForm.control}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}>
                    {intl.formatMessage({ id: 'optional-voucher' })}
                  </Checkbox>
                )}
              />

              <span
                css={{
                  color: '#7a7a7a',
                  display: 'block',
                  fontSize: '12px',
                  lineHeight: '18px',
                  paddingLeft: '24px',
                  marginTop: '4px',
                }}>
                {intl.formatMessage({ id: 'optional-voucher-description' })}
              </span>
            </div>

            {/* Card Type Selection */}
            {/*<div css={styles.formItem}>*/}
            {/*  <p css={styles.formLabel}>Card Type</p>*/}
            {/*  <Controller*/}
            {/*    name="voucher_type"*/}
            {/*    control={voucherForm.control}*/}
            {/*    render={({ field }) => (*/}
            {/*      <Radio.Group*/}
            {/*        {...field}*/}
            {/*        onChange={(e) => handleTypeChange(e.target.value)}*/}
            {/*        value={selectedType}>*/}
            {/*        <Radio value={1}>Unit Sum</Radio>*/}
            {/*        <Radio value={2}>Offer List</Radio>*/}
            {/*        <Radio value={3}>Upload Certified Document</Radio>*/}
            {/*        <Radio value={4}>Terms of Service</Radio>*/}
            {/*      </Radio.Group>*/}
            {/*    )}*/}
            {/*  />*/}
            {/*</div>*/}

            {/* Type 1 (UnitSumCard) specific fields */}
            {voucherData.voucher_type === 1 && (
              <Fragment>
                <div css={styles.formItem}>
                  <p css={styles.formLabel}>
                    {intl.formatMessage({ id: 'quantity' })}
                  </p>
                  <Controller
                    name="unit_count"
                    control={voucherForm.control}
                    render={({ field }) => (
                      <InputNumber
                        {...field}
                        min={0}
                        style={{ width: '100%' }}
                      />
                    )}
                  />
                </div>
                <div css={styles.formItem}>
                  <p css={styles.formLabel}>
                    {intl.formatMessage({ id: 'select-unit-type' })}
                  </p>
                  <Controller
                    name="unit_type"
                    control={voucherForm.control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        placeholder={intl.formatMessage({ id: 'select-unit' })}
                        style={{ width: '100%' }}
                        getPopupContainer={(trigger) => trigger.parentNode}>
                        {unitTypes.map((type) => (
                          <Option key={type.value} value={type.value}>
                            {type.label}
                          </Option>
                        ))}
                      </Select>
                    )}
                  />
                </div>
              </Fragment>
            )}
          </Fragment>
        )}
      </div>
    </Modal>
  );
};
